import { Clock3, Mail, MapPin, Phone } from 'lucide-react'
import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CharacterCounter, FieldError } from '../components/FormFieldFeedback'
import { PageHero } from '../components/PageHero'
import { Recaptcha } from '../components/Recaptcha'
import { FormsConfigurationError, getRecaptchaSiteKey } from '../config/api'
import { describedBy, formLimits, type FormFieldErrors } from '../config/formLimits'
import { FORMS_SITE_NAME } from '../config/formsSite'
import { images } from '../data/images'
import { contact } from '../data/site'
import { FormsApiError, submitContactForm } from '../services/formsApi'
import {
  containsWebLink,
  isSuspiciousMessage,
  isValidEmail,
  normalizePhone,
  trimmed,
} from '../utils/formValidation'

type Feedback =
  | {
      type: 'success' | 'error'
      message: string
    }
  | null

export function Contact() {
  const [searchParams] = useSearchParams()

  const initialSubject = useMemo(() => {
    const subject =
      searchParams.get('subject')?.trim() ?? ''

    return (
      subject.length >= 3 &&
      subject.length <= formLimits.subject &&
      !containsWebLink(subject)
    )
      ? subject
      : ''
  }, [searchParams])

  const [messageLength, setMessageLength] =
    useState(0)

  const [isSubmitting, setIsSubmitting] =
    useState(false)

  const [feedback, setFeedback] =
    useState<Feedback>(null)

  const [fieldErrors, setFieldErrors] =
    useState<FormFieldErrors>({})

  const [captchaToken, setCaptchaToken] =
    useState('')

  const [
    captchaResetKey,
    setCaptchaResetKey,
  ] = useState(0)

  const submitLock = useRef(false)

  const recaptchaSiteKey =
    getRecaptchaSiteKey()

  const clearFieldError = (
    field: string,
  ) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current
      }

      const next = {
        ...current,
      }

      delete next[field]

      return next
    })
  }

  const submit = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault()

    if (
      isSubmitting ||
      submitLock.current
    ) {
      return
    }

    const form = event.currentTarget
    const consentInput = form.elements.namedItem('consent') as HTMLInputElement | null
    if (!consentInput?.checked) {
      consentInput?.focus()
      setFeedback({
        type: 'error',
        message: 'Please agree to the Privacy Policy and contact consent before submitting.',
      })
      return
    }
    const data = new FormData(form)

    const name =
      trimmed(data.get('name'))

    const email =
      trimmed(
        data.get('email'),
      ).toLowerCase()

    const rawPhone =
      trimmed(data.get('phone'))

    const phone =
      normalizePhone(rawPhone)

    const subject =
      trimmed(data.get('subject'))

    const message =
      trimmed(data.get('message'))

    const website =
      trimmed(data.get('website'))

    const errors: FormFieldErrors = {}

    if (!name) {
      errors.name =
        'Name is required.'
    } else if (
      name.length > formLimits.name
    ) {
      errors.name =
        `Name must be ${formLimits.name} characters or fewer.`
    } else if (
      name.length < 2
    ) {
      errors.name =
        'Name must contain at least 2 characters.'
    }

    if (!email) {
      errors.email =
        'Email is required.'
    } else if (
      email.length > formLimits.email
    ) {
      errors.email =
        `Email must be ${formLimits.email} characters or fewer.`
    } else if (
      !isValidEmail(email)
    ) {
      errors.email =
        'Enter a valid email address.'
    }

    if (
      rawPhone.length >
      formLimits.phone
    ) {
      errors.phone =
        `Phone must be ${formLimits.phone} characters or fewer.`
    } else if (
      phone === null
    ) {
      errors.phone =
        'Enter a valid phone number.'
    }

    if (!subject) {
      errors.subject =
        'Subject is required.'
    } else if (
      subject.length >
      formLimits.subject
    ) {
      errors.subject =
        `Subject must be ${formLimits.subject} characters or fewer.`
    } else if (
      subject.length < 3
    ) {
      errors.subject =
        'Subject must contain at least 3 characters.'
    } else if (
      containsWebLink(subject)
    ) {
      errors.subject =
        'Subject cannot contain links.'
    }

    if (!message) {
      errors.message =
        'Message is required.'
    } else if (
      message.length >
      formLimits.message
    ) {
      errors.message =
        `Message must be ${formLimits.message.toLocaleString()} characters or fewer.`
    } else if (
      message.length < 10
    ) {
      errors.message =
        'Message must contain at least 10 characters.'
    } else if (
      isSuspiciousMessage(message)
    ) {
      errors.message =
        'Please remove excessive links or repeated text.'
    }

    if (!recaptchaSiteKey) {
      errors.recaptcha =
        'Human verification is unavailable.'
    } else if (!captchaToken) {
      errors.recaptcha =
        'Please complete the security check.'
    }

    if (
      Object.keys(errors).length >
      0
    ) {
      setFieldErrors(errors)

      setFeedback({
        type: 'error',
        message:
          'Please review the marked fields and complete the security check.',
      })

      return
    }

    submitLock.current = true
    setIsSubmitting(true)
    setFieldErrors({})
    setFeedback(null)

    try {
      await submitContactForm({
        formType: 'contact',
        websiteName:
          FORMS_SITE_NAME,
        name,
        email,
        phone: phone ?? '',
        subject,
        message,
        pageUrl:
          window.location.href,
        submittedAt:
          new Date().toISOString(),
        website,
        consent: (form.elements.namedItem('consent') as HTMLInputElement | null)?.checked === true,
        recaptchaToken:
          captchaToken,
      })

      form.reset()
      setMessageLength(0)

      setFeedback({
        type: 'success',
        message:
          'Thank you. Your message was sent successfully, and the salon will follow up soon.',
      })
    } catch (error) {
      if (
        error instanceof
          FormsApiError ||
        error instanceof
          FormsConfigurationError
      ) {
        setFeedback({
          type: 'error',
          message:
            error.userMessage,
        })

        setFieldErrors(
          error instanceof
            FormsApiError
            ? error.fieldErrors ??
                {}
            : {},
        )
      } else {
        setFeedback({
          type: 'error',
          message:
            'We could not send your message. Please try again or contact the salon directly.',
        })
      }
    } finally {
      setCaptchaToken('')

      setCaptchaResetKey(
        (value) => value + 1,
      )

      submitLock.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <PageHero
        eyebrow="We Would Love to Hear From You"
        title="Contact Us"
        image={
          images[
            'contact-banner'
          ]
        }
        objectPosition="center 54%"
      />

      <section className="section shell contact-grid">
        <div className="contact-intro reveal reveal--heading">
          <span className="eyebrow">
            Visit Diamond Salon
          </span>

          <h2>
            Let&apos;s plan your
            next appointment.
          </h2>

          <p>
            Contact the salon with
            service questions,
            appointment needs, or
            information about joining
            the team.
          </p>

          <ul className="contact-details">
            <li>
              <Clock3 />

              <div>
                <strong>
                  Salon Hours
                </strong>

                <span>
                  Tuesday - Saturday,
                  9am - 5pm
                </span>
              </div>
            </li>

            <li>
              <Mail />

              <div>
                <strong>
                  Email
                </strong>

                <a
                  href={`mailto:${contact.email}`}
                >
                  {contact.email}
                </a>
              </div>
            </li>

            <li>
              <Phone />

              <div>
                <strong>
                  Call Now
                </strong>

                <a
                  href={
                    contact.phoneHref
                  }
                >
                  {
                    contact.phoneDisplay
                  }
                </a>
              </div>
            </li>

            <li>
              <MapPin />

              <div>
                <strong>
                  Address
                </strong>

                <address>
                  <a
                    href={
                      contact.mapsUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {
                      contact.address
                    }
                  </a>
                </address>
              </div>
            </li>
          </ul>
        </div>

        <div className="contact-form-column reveal reveal--heading reveal--delay-1">
          <span className="eyebrow">
            Send a Note
          </span>

          <h2>
            Tell us how we can help.
          </h2>

          <form
            className="contact-form"
            onSubmit={submit}
            aria-busy={
              isSubmitting
            }
            noValidate
          >
            <p className="form-required-note">
              <span
                aria-hidden="true"
              >
                *
              </span>{' '}
              Required fields
            </p>

            <div className="field-row">
              <label>
                <span className="field-label">
                  Name{' '}
                  <span
                    aria-hidden="true"
                  >
                    *
                  </span>
                </span>

                <input
                  name="name"
                  type="text"
                  autoComplete="name"
                  minLength={2}
                  maxLength={
                    formLimits.name
                  }
                  required
                  aria-invalid={Boolean(
                    fieldErrors.name,
                  )}
                  aria-describedby={
                    fieldErrors.name
                      ? 'contact-name-error'
                      : undefined
                  }
                  onChange={() =>
                    clearFieldError(
                      'name',
                    )
                  }
                />

                <FieldError
                  id="contact-name-error"
                  message={
                    fieldErrors.name
                  }
                />
              </label>

              <label>
                <span className="field-label">
                  Email{' '}
                  <span
                    aria-hidden="true"
                  >
                    *
                  </span>
                </span>

                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  maxLength={
                    formLimits.email
                  }
                  required
                  aria-invalid={Boolean(
                    fieldErrors.email,
                  )}
                  aria-describedby={
                    fieldErrors.email
                      ? 'contact-email-error'
                      : undefined
                  }
                  onChange={() =>
                    clearFieldError(
                      'email',
                    )
                  }
                />

                <FieldError
                  id="contact-email-error"
                  message={
                    fieldErrors.email
                  }
                />
              </label>
            </div>

            <label>
              <span className="field-label">
                Phone{' '}
                <span className="field-optional">
                  Optional
                </span>
              </span>

              <input
                name="phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                maxLength={
                  formLimits.phone
                }
                aria-invalid={Boolean(
                  fieldErrors.phone,
                )}
                aria-describedby={
                  fieldErrors.phone
                    ? 'contact-phone-error'
                    : undefined
                }
                onChange={() =>
                  clearFieldError(
                    'phone',
                  )
                }
              />

              <FieldError
                id="contact-phone-error"
                message={
                  fieldErrors.phone
                }
              />
            </label>

            <label>
              <span className="field-label">
                Subject{' '}
                <span
                  aria-hidden="true"
                >
                  *
                </span>{' '}
                <span className="field-optional">
                  No links
                </span>
              </span>

              <input
                name="subject"
                type="text"
                defaultValue={
                  initialSubject
                }
                minLength={3}
                maxLength={
                  formLimits.subject
                }
                required
                aria-invalid={Boolean(
                  fieldErrors.subject,
                )}
                aria-describedby={
                  fieldErrors.subject
                    ? 'contact-subject-error'
                    : undefined
                }
                onChange={() =>
                  clearFieldError(
                    'subject',
                  )
                }
              />

              <FieldError
                id="contact-subject-error"
                message={
                  fieldErrors.subject
                }
              />
            </label>

            <label>
              <span className="field-label">
                How can we help?{' '}
                <span
                  aria-hidden="true"
                >
                  *
                </span>{' '}
                <span className="field-optional">
                  Please limit links to
                  one
                </span>
              </span>

              <textarea
                name="message"
                rows={7}
                minLength={10}
                maxLength={
                  formLimits.message
                }
                required
                aria-invalid={Boolean(
                  fieldErrors.message,
                )}
                aria-describedby={describedBy(
                  'contact-message-counter',
                  fieldErrors.message &&
                    'contact-message-error',
                )}
                onChange={(event) => {
                  setMessageLength(
                    event.target.value
                      .length,
                  )

                  clearFieldError(
                    'message',
                  )
                }}
              />

              <CharacterCounter
                id="contact-message-counter"
                current={
                  messageLength
                }
                maximum={
                  formLimits.message
                }
              />

              <FieldError
                id="contact-message-error"
                message={
                  fieldErrors.message
                }
              />
            </label>

            <label
              className="honeypot-field"
              aria-hidden="true"
            >
              Leave this field empty

              <input
                name="website"
                type="text"
                maxLength={200}
                tabIndex={-1}
                autoComplete="off"
              />
            </label>

            <Recaptcha
              onTokenChange={(
                token,
              ) => {
                setCaptchaToken(
                  token,
                )

                if (token) {
                  clearFieldError(
                    'recaptcha',
                  )
                }
              }}
              resetKey={
                captchaResetKey
              }
              theme="light"
            />

            <FieldError
              id="contact-recaptcha-error"
              message={
                fieldErrors.recaptcha
              }
            />

                        <label className="form-consent">
              <input
                name="consent"
                type="checkbox"
                required
                aria-required="true"
              />
              <span>
                I agree to the <a href="/privacy-policy">Privacy Policy</a> and consent to being contacted regarding this inquiry.
              </span>
            </label>
<button
              className="button"
              type="submit"
              disabled={
                isSubmitting ||
                !recaptchaSiteKey
              }
              aria-disabled={
                isSubmitting ||
                !recaptchaSiteKey
              }
            >
              {isSubmitting
                ? 'Sending...'
                : 'Send Message'}
            </button>

            <div
              className={`form-note${
                feedback
                  ? ` form-note--${feedback.type}`
                  : ''
              }`}
              role={
                feedback?.type ===
                'error'
                  ? 'alert'
                  : 'status'
              }
              aria-live="polite"
              aria-atomic="true"
            >
              {feedback?.message}
            </div>
          </form>
        </div>
      </section>
    </>
  )
}